"use client";

import type { RefObject } from "react";
import {
  DiagramFrame,
  Label,
  LegendRow,
  SHAPE,
} from "@/components/tools/geometry/diagram";

/*
 * A circle with its quarter turns named in degrees, radians and gradians, and the
 * angle drawn from the positive x-axis, with the angle's value in each unit beside it.
 */

export interface AngleDiagramProps {
  /** The angle brought into [0°, 360°). */
  degrees: number;
  title: string;
  svgRef: RefObject<SVGSVGElement | null>;
  /** The angle written in four units, one per legend row. */
  legend: string[];
}

const CX = 180;
const CY = 132;
const R = 74;
const FRAME_HEIGHT = 330;

const QUARTERS = [
  {
    degrees: 0,
    text: ["0°", "0", "0 gon"],
    anchor: "start",
    x: CX + R + 12,
    y: CY - 6,
  },
  {
    degrees: 90,
    text: ["90°", "π/2", "100 gon"],
    anchor: "middle",
    x: CX,
    y: CY - R - 42,
  },
  {
    degrees: 180,
    text: ["180°", "π", "200 gon"],
    anchor: "end",
    x: CX - R - 12,
    y: CY - 6,
  },
  {
    degrees: 270,
    text: ["270°", "3π/2", "300 gon"],
    anchor: "middle",
    x: CX,
    y: CY + R + 22,
  },
] as const;

const radians = (degrees: number) => (degrees * Math.PI) / 180;

export function AngleDiagram({
  degrees,
  title,
  svgRef,
  legend,
}: AngleDiagramProps) {
  const angle = radians(degrees);
  const end = { x: CX + R * Math.cos(angle), y: CY - R * Math.sin(angle) };
  const large = degrees > 180 ? 1 : 0;
  const arcR = 26;
  const arcEnd = {
    x: CX + arcR * Math.cos(angle),
    y: CY - arcR * Math.sin(angle),
  };
  const wedge =
    degrees < 0.01
      ? ""
      : `M${CX} ${CY} L${CX + R} ${CY} A${R} ${R} 0 ${large} 0 ${end.x} ${end.y} Z`;
  return (
    <DiagramFrame height={FRAME_HEIGHT} svgRef={svgRef} title={title}>
      <circle
        cx={CX}
        cy={CY}
        fill="none"
        r={R}
        stroke={SHAPE.ink}
        strokeWidth={2}
      />
      {wedge === "" ? null : (
        <path d={wedge} fill={SHAPE.fill} fillOpacity={0.18} />
      )}
      {QUARTERS.map((quarter) => {
        const a = radians(quarter.degrees);
        return (
          <g key={quarter.degrees}>
            <line
              stroke={SHAPE.ink}
              strokeWidth={2}
              x1={CX + (R - 6) * Math.cos(a)}
              x2={CX + (R + 4) * Math.cos(a)}
              y1={CY - (R - 6) * Math.sin(a)}
              y2={CY - (R + 4) * Math.sin(a)}
            />
            {quarter.text.map((line, index) => (
              <Label
                anchor={quarter.anchor}
                color={index === 0 ? SHAPE.ink : SHAPE.muted}
                key={line}
                size={index === 0 ? 12 : 10.5}
                x={quarter.x}
                y={quarter.y + index * 12}
              >
                {line}
              </Label>
            ))}
          </g>
        );
      })}
      <line
        stroke={SHAPE.accent}
        strokeWidth={3}
        x1={CX}
        x2={end.x}
        y1={CY}
        y2={end.y}
      />
      <line
        stroke={SHAPE.stroke}
        strokeWidth={3}
        x1={CX}
        x2={CX + R}
        y1={CY}
        y2={CY}
      />
      {degrees < 0.01 ? null : (
        <path
          d={`M${CX + arcR} ${CY} A${arcR} ${arcR} 0 ${large} 0 ${arcEnd.x} ${arcEnd.y}`}
          fill="none"
          stroke={SHAPE.accent}
          strokeWidth={2.5}
        />
      )}
      <circle cx={CX} cy={CY} fill={SHAPE.ink} r={3} />
      {legend.map((text, index) => (
        <LegendRow
          key={text}
          sample={
            <path
              d="M0 6 A12 12 0 0 1 20 -2"
              fill="none"
              stroke={SHAPE.accent}
              strokeWidth={2.5}
            />
          }
          text={text}
          x={index % 2 === 0 ? 40 : 200}
          y={286 + Math.floor(index / 2) * 28}
        />
      ))}
    </DiagramFrame>
  );
}
